import React from 'react';
import type { AdminReviewMessageAttachment } from '../../types/admin';

interface AdminMessageAttachmentsProps {
  attachments?: AdminReviewMessageAttachment[] | null;
}

function getAttachmentName(attachment: AdminReviewMessageAttachment, index: number): string {
  return (
    attachment.originalName ||
    attachment.originalFileName ||
    attachment.name ||
    attachment.fileName ||
    `Attachment ${index + 1}`
  );
}

function getAttachmentUrl(attachment: AdminReviewMessageAttachment): string | null {
  return attachment.url || attachment.downloadUrl || null;
}

function isImageAttachment(attachment: AdminReviewMessageAttachment): boolean {
  const type = attachment.mimeType || attachment.contentType || '';
  if (type.toLowerCase().startsWith('image/')) return true;

  const name = getAttachmentName(attachment, 0).toLowerCase();
  return /\.(avif|gif|jpe?g|png|webp)$/.test(name);
}

function formatAttachmentSize(size?: number | null): string | null {
  if (!size || size <= 0) return null;
  if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

const AdminMessageAttachments: React.FC<AdminMessageAttachmentsProps> = ({ attachments }) => {
  if (!attachments?.length) return null;

  return (
    <div className="admin-message-attachments">
      {attachments.map((attachment, index) => {
        const name = getAttachmentName(attachment, index);
        const url = getAttachmentUrl(attachment);
        const size = formatAttachmentSize(attachment.size);
        const key = attachment.id || `${name}-${index}`;

        return (
          <div className="admin-message-attachment" key={key}>
            {url && isImageAttachment(attachment) ? (
              <a className="admin-message-attachment-image-link" href={url} rel="noreferrer" target="_blank">
                <img alt={name} src={url} />
              </a>
            ) : null}
            <div className="admin-message-attachment-meta">
              <strong>{name}</strong>
              <small>{attachment.mimeType || attachment.contentType || 'Attached file'}{size ? ` - ${size}` : ''}</small>
              {url ? (
                <a href={url} rel="noreferrer" target="_blank">
                  Open attachment
                </a>
              ) : (
                <span>Attachment URL unavailable</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AdminMessageAttachments;
